import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { environment } from 'environments/environment';

export type ImpersonationState = {
    active: boolean;
    impersonatorUserId: number;
    impersonatedUserId: number;
    originalAccessToken: string;
    originalUser: User;
};

@Injectable()
export class AuthService
{
    private _authenticated: boolean = false;
    private baseUrl: string;
    private impersonationStorageKey: string = 'impersonation';
    private _impersonation: BehaviorSubject<ImpersonationState | null> = new BehaviorSubject<ImpersonationState | null>(this._getStoredImpersonation());

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService
    )
    {
        this.baseUrl = `${environment.apiURL}/auth`;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string)
    {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string
    {
        return localStorage.getItem('accessToken') ?? '';
    }

    get impersonation$(): Observable<ImpersonationState | null>
    {
        return this._impersonation.asObservable();
    }

    get impersonation(): ImpersonationState | null
    {
        return this._impersonation.value;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(resetEmail: {email: string}): Observable<any>
    {
        return this._httpClient.post(this.baseUrl + '/forgot-password', resetEmail);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(credentials: { email: string; password: string; password_confirmation: string; token: string }): Observable<any>
    {
        return this._httpClient.post(this.baseUrl + '/reset-password', credentials);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string; role_new:number }): Observable<any>
    {
        // Throw error, if the user is already logged in
        if ( this._authenticated )
        {
            return throwError('Usuario actualmente cuenta con una sesión iniciada.');
        }

        this._clearImpersonation();

        return this._httpClient.post(this.baseUrl + '/sign-in', credentials).pipe(
            switchMap((response: any) => {

                // Store the access token in the local storage
                this.accessToken = response.accessToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any>
    {
        // Renew token
        return this._httpClient.post(this.baseUrl + '/refresh-access-token', {
            accessToken: this.accessToken
        }).pipe(
            catchError(() =>

                // Return false
                of(false)
            ),
            switchMap((response: any) => {

                if(response.accessToken) {
                    // Store the access token in the local storage
                    this.accessToken = response.accessToken;

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.user;

                    // Return true
                    return of(true);
                } else {

                    // Remove the access token from the local storage
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('filterRole');
                    this._clearImpersonation();

                    // Set the authenticated flag to false
                    this._authenticated = false;

                    // Return false
                    return of(false)
                }
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Revoke token if authenticated
        if(this._authenticated){
            const req = this._httpClient.post(this.baseUrl + '/sign-out', {
                accessToken: this.accessToken
            });
            req.subscribe(); // post http observable is lazy, need to subscribe to execute the request
        }

        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('filterRole');
        this._clearImpersonation();

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any>
    {
        return this._httpClient.post(this.baseUrl + '/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any>
    {
        return this._httpClient.post(this.baseUrl + '/unlock-session', credentials).pipe(
            switchMap((response: any) => {

                // Store the access token in the local storage
                this.accessToken = response.accessToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Start user impersonation
     */
    impersonate(userId: number): Observable<any>
    {
        if ( this.impersonation?.active )
        {
            return throwError('Ya existe una impersonacion activa.');
        }

        const originalAccessToken = this.accessToken;

        return this._userService.user$.pipe(
            take(1),
            switchMap((originalUser: User) => this._httpClient.post(`${this.baseUrl}/impersonate/${userId}`, null).pipe(
                tap((response: any) => {
                    const impersonation: ImpersonationState = {
                        active: true,
                        impersonatorUserId: response.impersonation.impersonator_user_id,
                        impersonatedUserId: response.impersonation.impersonated_user_id,
                        originalAccessToken,
                        originalUser
                    };

                    this._setImpersonation(impersonation);
                    this.accessToken = response.accessToken;
                    this._authenticated = true;
                    this._userService.user = response.user;
                })
            ))
        );
    }

    /**
     * Stop user impersonation
     */
    stopImpersonation(): Observable<any>
    {
        const impersonation = this.impersonation;

        if ( !impersonation?.active )
        {
            return throwError('No existe una impersonacion activa.');
        }

        return this._httpClient.post(`${this.baseUrl}/impersonate/stop`, null).pipe(
            tap(() => this._restoreOriginalSession(impersonation)),
            catchError((error) => {
                if ( error?.status === 400 && impersonation.originalAccessToken && impersonation.originalUser )
                {
                    this._restoreOriginalSession(impersonation);
                    return of({message: 'Impersonation stopped', fallback: true});
                }

                return throwError(error);
            })
        );
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        if ( this._authenticated )
        {
            return of(true);
        }

        // Check the access token availability
        if ( !this.accessToken )
        {
            this._clearImpersonation();
            return of(false);
        }

        // Check the access token expire date
        if ( AuthUtils.isTokenExpired(this.accessToken) )
        {
            this._clearImpersonation();
            return of(false);
        }

        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    private _setImpersonation(impersonation: ImpersonationState): void
    {
        localStorage.setItem(this.impersonationStorageKey, JSON.stringify(impersonation));
        this._impersonation.next(impersonation);
    }

    private _clearImpersonation(): void
    {
        localStorage.removeItem(this.impersonationStorageKey);
        this._impersonation.next(null);
    }

    private _restoreOriginalSession(impersonation: ImpersonationState): void
    {
        this.accessToken = impersonation.originalAccessToken;
        this._authenticated = true;
        this._userService.user = impersonation.originalUser;
        this._clearImpersonation();
    }

    private _getStoredImpersonation(): ImpersonationState | null
    {
        const storedImpersonation = localStorage.getItem(this.impersonationStorageKey);

        if ( !storedImpersonation )
        {
            return null;
        }

        if ( !localStorage.getItem('accessToken') )
        {
            localStorage.removeItem(this.impersonationStorageKey);
            return null;
        }

        try
        {
            const impersonation = JSON.parse(storedImpersonation);
            return impersonation?.active ? impersonation : null;
        }
        catch (error)
        {
            localStorage.removeItem(this.impersonationStorageKey);
            return null;
        }
    }
}
